import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import domtoimage from 'dom-to-image';

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {

  /**
   * Exporta a PDF usando jsPDF, html2canvas y dom-to-image
   * Optimizado para gráficos de ngx-charts en reportes, siguiendo mejores prácticas.
   */
  exportToPdf(selector: string, filename: string = 'reporte.pdf'): Promise<void> {
    return new Promise(async (resolve) => {
      const container = document.querySelector(selector) as HTMLElement;
      if (!container) {
        console.error('Elemento no encontrado:', selector);
        resolve();
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const margin = 10;
      const usableWidth = pageWidth - 2 * margin;
      const pageHeight = 297;

      // 🔹 Selecciona secciones específicas para reportes (gráficos, tablas, etc.)
      const sections = Array.from(
        container.querySelectorAll('.guia3-report, .debug--preview, .detalle')
      ) as HTMLElement[];

      // Si no hay secciones específicas, exporta todo el contenedor
      if (sections.length === 0) {
        sections.push(container);
      }

      // 🔹 Ocultar elementos dinámicos (tooltips, overlays) durante captura
      const hiddenElements: HTMLElement[] = [];
      container.querySelectorAll('.ngx-charts-tooltip, .cdk-overlay-backdrop').forEach(el => {
        if (el instanceof HTMLElement) {
          hiddenElements.push(el);
          el.style.display = 'none';
        }
      });

      // 🔹 Ocultar scrollbars y asegurar renderizado completo
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // 🔹 Esperar a que los gráficos se rendericen completamente (mejor práctica para ngx-charts)
      await this.waitForChartsToRender(sections);

      let currentY = margin;

      for (const [i, section] of sections.entries()) {
        try {
          // 🔹 Preparar sección para captura (evitar cortes)
          section.style.breakInside = 'avoid';
          section.style.pageBreakInside = 'avoid';
          section.style.overflow = 'visible';
          section.style.position = 'relative'; // Mejor para SVGs

          // 🔹 Usar dom-to-image primero (mejor para SVGs de ngx-charts)
          let dataUrl: string;
          try {
            dataUrl = await domtoimage.toPng(section, {
              quality: 1,
              bgcolor: '#ffffff',
              width: section.scrollWidth || section.offsetWidth,
              height: section.scrollHeight || section.offsetHeight,
              filter: (node: any) => {
                // Excluir elementos no deseados
                if (node instanceof Element) {
                  return !node.classList.contains('ngx-charts-tooltip') &&
                         !node.classList.contains('cdk-overlay-backdrop');
                }
                return true;
              }
            } as any); // Cast to any to allow scale if not in types
          } catch (domToImageError) {
            console.warn('dom-to-image falló, usando html2canvas como fallback:', domToImageError);
            // Fallback a html2canvas con configuraciones optimizadas para gráficos
            const canvas = await html2canvas(section, {
              scale: 2,
              useCORS: true,
              backgroundColor: '#ffffff',
              allowTaint: false,
              logging: false,
              width: section.scrollWidth || section.offsetWidth,
              height: section.scrollHeight || section.offsetHeight,
              ignoreElements: (element) => {
                // Ignorar tooltips y overlays
                return element.classList.contains('ngx-charts-tooltip') ||
                       element.classList.contains('cdk-overlay-backdrop');
              }
            });
            dataUrl = canvas.toDataURL('image/png');
          }

          // 🔹 Crear imagen y calcular dimensiones
          const img = new Image();
          await new Promise<void>((imgResolve, reject) => {
            img.onload = () => {
              const imgWidth = usableWidth;
              const imgHeight = (img.height * imgWidth) / img.width;

              // 🔹 Verificar si cabe en la página actual (solo agregar página si hay contenido previo)
              if (currentY > margin && currentY + imgHeight > pageHeight - margin) {
                pdf.addPage();
                currentY = margin;
              }

              // 🔹 Ajustar altura si excede la página (escalar si es necesario)
              let finalHeight = imgHeight;
              if (currentY + imgHeight > pageHeight - margin) {
                finalHeight = pageHeight - currentY - margin;
              }

              pdf.addImage(
                dataUrl,
                'PNG',
                margin,
                currentY,
                imgWidth,
                finalHeight
              );

              currentY += finalHeight + 5; // Espacio entre secciones
              imgResolve();
            };
            img.onerror = () => reject(new Error('Error cargando imagen para PDF'));
            img.src = dataUrl;
          });

        } catch (err) {
          console.error('Error exportando sección:', i, err);
          // Continuar con la siguiente sección en lugar de fallar completamente
        }
      }

      // 🔹 Restaurar elementos ocultos
      hiddenElements.forEach(el => el.style.display = '');

      pdf.save(filename);
      document.body.style.overflow = originalOverflow;
      resolve();
    });
  }

  /**
   * Espera a que los gráficos de ngx-charts se rendericen completamente.
   * Mejor práctica para asegurar captura precisa de SVGs.
   */
  private async waitForChartsToRender(sections: HTMLElement[]): Promise<void> {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 10;
      const checkInterval = 100;

      const check = () => {
        attempts++;
        const allRendered = sections.every(section => {
          const svgs = section.querySelectorAll('svg');
          return svgs.length > 0 && Array.from(svgs).every(svg => svg.getBoundingClientRect().width > 0);
        });

        if (allRendered || attempts >= maxAttempts) {
          resolve();
        } else {
          setTimeout(check, checkInterval);
        }
      };

      check();
    });
  }

  /**
   * Exporta un gráfico individual a PDF (útil para reportes específicos).
   */
  exportChartToPdf(chartSelector: string, filename: string = 'chart.pdf'): Promise<void> {
    return this.exportToPdf(chartSelector, filename);
  }
}
